/**
 * CustomerProfilePanel - Right panel showing matched customer data
 * Displays customer info, vehicles, and active bookings
 */

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { User, Car, Calendar, Plus, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CustomerByPhoneResult } from '@leal-crm/shared';

interface CustomerProfilePanelProps {
  match: {
    status: 'matched' | 'multiple' | 'not_found';
    customer?: CustomerByPhoneResult['customer'];
    customers?: CustomerByPhoneResult['customer'][];
    vehicles?: CustomerByPhoneResult['vehicles'];
    activeBookings?: CustomerByPhoneResult['activeBookings'];
  } | null;
  contactPhone: string;
  isLoading: boolean;
  onCreateCustomer?: () => void;
  onSelectCustomer?: (customerId: string) => void;
}

export function CustomerProfilePanel({
  match,
  contactPhone,
  isLoading,
  onCreateCustomer,
  onSelectCustomer,
}: CustomerProfilePanelProps) {
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <p className="text-sm">Selecciona una conversación para ver el perfil del cliente</p>
      </div>
    );
  }

  // Not found
  if (match.status === 'not_found') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <div className="rounded-full bg-muted p-4">
          <User className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="text-center">
          <h3 className="font-medium">Cliente no encontrado</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            No hay ningún cliente con el teléfono {contactPhone}
          </p>
        </div>
        {onCreateCustomer && (
          <Button onClick={onCreateCustomer} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Crear cliente
          </Button>
        )}
      </div>
    );
  }

  // Multiple matches
  if (match.status === 'multiple' && match.customers) {
    return (
      <div className="flex h-full flex-col gap-4 p-6">
        <h3 className="font-medium">Múltiples clientes encontrados</h3>
        <p className="text-sm text-muted-foreground">
          Hay {match.customers.length} clientes con este número de teléfono
        </p>
        <div className="space-y-2">
          {match.customers.map((customer) => (
            <Card
              key={customer.id}
              className="cursor-pointer transition-colors hover:bg-accent"
              onClick={() => onSelectCustomer?.(customer.id)}
            >
              <CardContent className="flex items-center gap-3 p-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{customer.name}</p>
                  <p className="text-xs text-muted-foreground">{customer.email}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Single match - show full profile
  const customer = match.customer;
  if (!customer) return null;

  return (
    <div className="h-full overflow-y-auto">
      {/* Customer Info */}
      <Card className="m-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              {customer.name}
            </CardTitle>
            <Link to={`/customers/${customer.id}`}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          {customer.email && (
            <p className="text-muted-foreground">{customer.email}</p>
          )}
          <p className="text-muted-foreground">{customer.phone}</p>
        </CardContent>
      </Card>

      {/* Active Bookings */}
      {match.activeBookings && match.activeBookings.length > 0 && (
        <Card className="mx-4 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              Reservas activas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {match.activeBookings.map((booking) => (
              <Link
                key={booking.id}
                to={`/bookings/${booking.id}`}
                className="flex items-center justify-between rounded-md border p-2 transition-colors hover:bg-accent"
              >
                <div>
                  <p className="font-medium text-sm">{booking.reference}</p>
                  <p className="text-xs text-muted-foreground">{booking.type}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {booking.status}
                </Badge>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Vehicles */}
      {match.vehicles && match.vehicles.length > 0 && (
        <Card className="mx-4 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-4 w-4" />
              Vehículos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {match.vehicles.map((vehicle) => (
              <Link
                key={vehicle.id}
                to={`/vehicles/${vehicle.id}`}
                className="flex items-center justify-between rounded-md border p-2 transition-colors hover:bg-accent"
              >
                <div>
                  <p className="font-medium text-sm">{vehicle.plate}</p>
                  <p className="text-xs text-muted-foreground">
                    {vehicle.brand} {vehicle.model} {vehicle.year || ''}
                  </p>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
